import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import pdfService from '../services/pdfService';
import './ReportsExport.css';

const ReportsExport = ({ user }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState({});
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [weekStart, setWeekStart] = useState(
        new Date(Date.now() - new Date().getDay() * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    );

    const reports = [
        {
            id: 'bookings',
            title: t('reports.bookingsReport'),
            description: t('reports.bookingsDesc'),
            icon: '📅',
            color: '#3b82f6',
            requiresDateRange: true,
            action: async () => {
                const blob = await pdfService.generateBookingsReport(
                    new Date(dateRange.startDate),
                    new Date(dateRange.endDate)
                );
                pdfService.downloadBlob(blob, `reservas_${dateRange.startDate}_${dateRange.endDate}.html`);
            }
        },
        {
            id: 'user',
            title: t('reports.userReport'),
            description: t('reports.userDesc'),
            icon: '👤',
            color: '#8b5cf6',
            requiresDateRange: false,
            action: async () => {
                const blob = await pdfService.generateUserReport(user.id);
                pdfService.downloadBlob(blob, `perfil_${user.name || 'utilizador'}.html`);
            }
        },
        {
            id: 'revenue',
            title: t('reports.revenueReport'),
            description: t('reports.revenueDesc'),
            icon: '💰',
            color: '#22c55e',
            requiresDateRange: true,
            adminOnly: true,
            action: async () => {
                const blob = await pdfService.generateRevenueReport(
                    new Date(dateRange.startDate),
                    new Date(dateRange.endDate)
                );
                pdfService.downloadBlob(blob, `receitas_${dateRange.startDate}_${dateRange.endDate}.html`);
            }
        },
        {
            id: 'attendance',
            title: t('reports.attendanceReport'),
            description: t('reports.attendanceDesc'),
            icon: '📊',
            color: '#f59e0b',
            requiresDateRange: true,
            adminOnly: true,
            action: async () => {
                const blob = await pdfService.generateAttendanceReport(
                    new Date(dateRange.startDate),
                    new Date(dateRange.endDate)
                );
                pdfService.downloadBlob(blob, `assiduidade_${dateRange.startDate}_${dateRange.endDate}.html`);
            }
        },
        {
            id: 'leaderboard',
            title: t('reports.leaderboardReport'),
            description: t('reports.leaderboardDesc'),
            icon: '🏆',
            color: '#ec4899',
            requiresDateRange: false,
            action: async () => {
                const blob = await pdfService.generateLeaderboard();
                pdfService.downloadBlob(blob, 'leaderboard.html');
            }
        },
        {
            id: 'schedule',
            title: t('reports.scheduleReport'),
            description: t('reports.scheduleDesc'),
            icon: '🗓️',
            color: '#06b6d4',
            requiresWeek: true,
            action: async () => {
                const blob = await pdfService.generateSchedule(new Date(weekStart));
                pdfService.downloadBlob(blob, `horario_${weekStart}.html`);
            }
        }
    ];

    const handleExport = async (report) => {
        setLoading(prev => ({ ...prev, [report.id]: true }));
        
        try {
            await report.action();
        } catch (error) {
            console.error('Export error:', error);
            alert(t('errors.exportFailed'));
        } finally {
            setLoading(prev => ({ ...prev, [report.id]: false }));
        }
    };

    const handlePreview = async (report) => {
        setLoading(prev => ({ ...prev, [`${report.id}_preview`]: true }));
        
        try {
            let blob;
            switch (report.id) {
                case 'bookings':
                    blob = await pdfService.generateBookingsReport(
                        new Date(dateRange.startDate),
                        new Date(dateRange.endDate)
                    );
                    break;
                case 'user':
                    blob = await pdfService.generateUserReport(user.id);
                    break;
                case 'revenue':
                    blob = await pdfService.generateRevenueReport(
                        new Date(dateRange.startDate),
                        new Date(dateRange.endDate)
                    );
                    break;
                case 'attendance':
                    blob = await pdfService.generateAttendanceReport(
                        new Date(dateRange.startDate),
                        new Date(dateRange.endDate)
                    );
                    break;
                case 'leaderboard':
                    blob = await pdfService.generateLeaderboard();
                    break;
                case 'schedule':
                    blob = await pdfService.generateSchedule(new Date(weekStart));
                    break;
                default:
                    return;
            }
            pdfService.openBlob(blob);
        } catch (error) {
            console.error('Preview error:', error);
            alert(t('errors.previewFailed'));
        } finally {
            setLoading(prev => ({ ...prev, [`${report.id}_preview`]: false }));
        }
    };

    const filteredReports = reports.filter(r => 
        !r.adminOnly || (user && (user.role === 'Admin' || user.role === 'Trainer'))
    );

    return (
        <div className="reports-export">
            <h2 className="page-title">
                <span className="icon">📄</span>
                {t('reports.exportReports')}
            </h2>

            {/* Date Range Selector */}
            <div className="date-range-selector">
                <div className="date-group">
                    <label>{t('reports.startDate')}</label>
                    <input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                </div>
                <div className="date-separator">→</div>
                <div className="date-group">
                    <label>{t('reports.endDate')}</label>
                    <input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                </div>
            </div>

            {/* Week Selector */}
            <div className="week-selector">
                <label>{t('reports.weekStart')}</label>
                <input
                    type="date"
                    value={weekStart}
                    onChange={(e) => setWeekStart(e.target.value)}
                />
            </div>

            {/* Reports Grid */}
            <div className="reports-grid">
                {filteredReports.map((report) => (
                    <div 
                        key={report.id} 
                        className="report-card"
                        style={{ '--card-color': report.color }}
                    >
                        <div className="report-icon" style={{ background: report.color }}>
                            {report.icon}
                        </div>
                        <div className="report-info">
                            <h3>{report.title}</h3>
                            <p>{report.description}</p>
                            {report.requiresDateRange && (
                                <span className="date-badge">
                                    📅 {dateRange.startDate} - {dateRange.endDate}
                                </span>
                            )}
                            {report.requiresWeek && (
                                <span className="date-badge">
                                    📅 {t('reports.weekOf')} {weekStart}
                                </span>
                            )}
                        </div>
                        <div className="report-actions">
                            <button
                                className="btn-preview"
                                onClick={() => handlePreview(report)}
                                disabled={loading[`${report.id}_preview`]}
                            >
                                {loading[`${report.id}_preview`] ? (
                                    <span className="spinner"></span>
                                ) : (
                                    <>👁️ {t('reports.preview')}</>
                                )}
                            </button>
                            <button
                                className="btn-download"
                                onClick={() => handleExport(report)}
                                disabled={loading[report.id]}
                            >
                                {loading[report.id] ? (
                                    <span className="spinner"></span>
                                ) : (
                                    <>⬇️ {t('reports.download')}</>
                                )}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Print Tip */}
            <div className="print-tip">
                <span className="icon">💡</span>
                <span>{t('reports.printTip')}</span>
            </div>
        </div>
    );
};

export default ReportsExport;
